import React from 'react';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import ChatMessageDisplay from './ChatMessageDisplay';
import {
  Contact,
  ChatMessage,
  ChatMessageType,
  ChatParticipantStateChangeType,
  ChatParticipantType,
} from '../../../api/ContactListening/schema';
import Divider from '@amzn/meridian/divider';
import Column from '@amzn/meridian/column';
import { FormattedMessage } from 'react-intl';
import { getLocaleStringFormatted } from '../../../translations';
import CoachingEmptyState from '../../../Assets/coachingEmptyState.png';
import { cloneDeep } from 'lodash';

export interface ChatMediumProps {
  contact: Contact | undefined;
}

export const ChatMedium: React.FC<ChatMediumProps> = ({ contact }) => {
  if (!contact?.medium?.chat?.messages) {
    return (
      <Column alignmentHorizontal='center' alignmentVertical='center'>
        <div>
          <img src={CoachingEmptyState} alt='no contacts' />
        </div>
        <Text>
          <FormattedMessage
            id='cs_perfportal_contact_listening_no_chat_history'
            defaultMessage='There is no chat history to display here at the moment.'
          />
        </Text>
      </Column>
    );
  }
  const chat = contact.medium.chat;
  const isSameDate = (
    timestamp1: string,
    timestamp2: string | undefined
  ): boolean => {
    if (timestamp2) {
      return timestamp1.substring(0, 10) === timestamp2.substring(0, 10);
    }
    return false;
  };

  const renderMessages = (messages: ChatMessage[]): JSX.Element[] => {
    let lastMessage: ChatMessage | undefined = undefined;

    const results: JSX.Element[] = [];
    for (const message of messages) {
      if (!isCustomerConnectedMessage(message)) {
        const sameDate = isSameDate(message.timestamp, lastMessage?.timestamp);

        if (!sameDate) {
          const timestamp = new Date(message.timestamp);
          results.push(
            <Column alignmentHorizontal='center'>
              <Text type='b300' color='secondary'>
                {timestamp.toLocaleDateString(getLocaleStringFormatted(), {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Text>
            </Column>
          );
        }

        const messageDisplay = (
          <ChatMessageDisplay
            chatMessage={stripHtmlTagsFromMessage(message)}
            prevMessage={lastMessage}
            customerTimezone={getLocaleStringFormatted()}
          />
        );
        results.push(messageDisplay);
        lastMessage = message;
      }
    }

    return results;
  };

  const stripHtmlTagsFromMessage = (message: ChatMessage): ChatMessage => {
    const mutableMessage = cloneDeep(message);
    if (mutableMessage?.chatText) {
      const strippedMessageText = mutableMessage.chatText.text.replace(
        /<[^<>]+>/gi,
        ''
      );
      mutableMessage.chatText.text = strippedMessageText;
    }

    return mutableMessage;
  };

  const isCustomerConnectedMessage = (message: ChatMessage): boolean => {
    return (
      message.type === ChatMessageType.PARTICIPANT_CHANGE &&
      message.stateChangeType === ChatParticipantStateChangeType.CONNECTED &&
      message.participant?.__typename === ChatParticipantType.CUSTOMER
    );
  };

  return (
    <Box overflowY='scroll' maxHeight='600px' width='fill' spacingInset='200'>
      <Divider />
      {renderMessages(chat.messages!)}
      <Divider />
    </Box>
  );
};
export default ChatMedium;
